import type { AnalysisReport, AnalyzeProjectInput, GitignoreSourceInput } from '../types';

export const ANALYSIS_WORKER_PROTOCOL_VERSION = 1 as const;

export type AnalysisProgressPhase = 'queued' | 'analyzing' | 'complete';

export interface AnalysisProgress {
  readonly phase: AnalysisProgressPhase;
  readonly completed: number;
  readonly total: number;
}

export interface AnalyzeWorkerRequest {
  readonly protocolVersion: typeof ANALYSIS_WORKER_PROTOCOL_VERSION;
  readonly type: 'analyze';
  readonly requestId: string;
  readonly input: AnalyzeProjectInput;
}

export interface AnalysisWorkerProgressMessage {
  readonly protocolVersion: typeof ANALYSIS_WORKER_PROTOCOL_VERSION;
  readonly type: 'progress';
  readonly requestId: string;
  readonly progress: AnalysisProgress;
}

export interface AnalysisWorkerSuccessMessage {
  readonly protocolVersion: typeof ANALYSIS_WORKER_PROTOCOL_VERSION;
  readonly type: 'success';
  readonly requestId: string;
  readonly report: AnalysisReport;
}

export type AnalysisWorkerErrorCode = 'invalid-request' | 'analysis-failed';

export interface SerializedAnalysisError {
  readonly code: AnalysisWorkerErrorCode;
  readonly name: string;
  readonly message: string;
  readonly stack?: string;
}

export interface AnalysisWorkerErrorMessage {
  readonly protocolVersion: typeof ANALYSIS_WORKER_PROTOCOL_VERSION;
  readonly type: 'error';
  readonly requestId: string;
  readonly error: SerializedAnalysisError;
}

export type AnalysisWorkerResponse =
  AnalysisWorkerProgressMessage | AnalysisWorkerSuccessMessage | AnalysisWorkerErrorMessage;

export function createAnalyzeWorkerRequest(
  requestId: string,
  input: AnalyzeProjectInput,
): AnalyzeWorkerRequest {
  return {
    protocolVersion: ANALYSIS_WORKER_PROTOCOL_VERSION,
    type: 'analyze',
    requestId,
    input,
  };
}

export function isAnalyzeWorkerRequest(value: unknown): value is AnalyzeWorkerRequest {
  if (!isRecord(value) || value.protocolVersion !== ANALYSIS_WORKER_PROTOCOL_VERSION) {
    return false;
  }

  return (
    value.type === 'analyze' &&
    typeof value.requestId === 'string' &&
    value.requestId.length > 0 &&
    isAnalyzeInput(value.input)
  );
}

export function isAnalysisWorkerResponse(value: unknown): value is AnalysisWorkerResponse {
  if (
    !isRecord(value) ||
    value.protocolVersion !== ANALYSIS_WORKER_PROTOCOL_VERSION ||
    typeof value.requestId !== 'string'
  ) {
    return false;
  }

  if (value.type === 'progress') {
    return isProgress(value.progress);
  }

  if (value.type === 'success') {
    return isRecord(value.report);
  }

  return value.type === 'error' && isSerializedError(value.error);
}

export function requestIdFromUnknown(value: unknown): string {
  if (isRecord(value) && typeof value.requestId === 'string' && value.requestId.length > 0) {
    return value.requestId;
  }
  return 'unknown';
}

export function serializeAnalysisError(
  error: unknown,
  code: AnalysisWorkerErrorCode,
): SerializedAnalysisError {
  if (error instanceof Error) {
    return {
      code,
      name: error.name,
      message: error.message,
      ...(error.stack === undefined ? {} : { stack: error.stack }),
    };
  }

  return {
    code,
    name: 'Error',
    message: typeof error === 'string' ? error : 'The analysis failed for an unknown reason.',
  };
}

function isAnalyzeInput(value: unknown): value is AnalyzeProjectInput {
  if (!isRecord(value) || !Array.isArray(value.sources) || !Array.isArray(value.paths)) {
    return false;
  }

  return value.sources.every(isGitignoreSource) && value.paths.every(isString);
}

function isGitignoreSource(value: unknown): value is GitignoreSourceInput {
  return isRecord(value) && typeof value.path === 'string' && typeof value.content === 'string';
}

function isProgress(value: unknown): value is AnalysisProgress {
  return (
    isRecord(value) &&
    (value.phase === 'queued' || value.phase === 'analyzing' || value.phase === 'complete') &&
    typeof value.completed === 'number' &&
    Number.isFinite(value.completed) &&
    typeof value.total === 'number' &&
    Number.isFinite(value.total)
  );
}

function isSerializedError(value: unknown): value is SerializedAnalysisError {
  return (
    isRecord(value) &&
    (value.code === 'invalid-request' || value.code === 'analysis-failed') &&
    typeof value.name === 'string' &&
    typeof value.message === 'string' &&
    (value.stack === undefined || typeof value.stack === 'string')
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}
